// Global keyboard hook (Unit 36d, spec §11.12).
//
// A single keydown listener with focus-aware filtering. Single-letter
// shortcuts no-op when an `<input>`, `<textarea>`, or contenteditable
// element is focused; modifier-prefixed shortcuts always fire. Each hook
// call subscribes to one specific binding; the hook list at the call site
// is the source of truth for which shortcuts a view supports.

import { useEffect } from "react";

interface Binding {
  /** Single key (lowercase) or special name like "Escape", "ArrowDown". */
  key: string;
  /** Require ⌘/Ctrl. */
  meta?: boolean;
  /** Require Shift. */
  shift?: boolean;
  /** Allow firing even when an input/textarea is focused. Defaults to false. */
  allowInInput?: boolean;
  /** What to run when the key matches. Receive the original event. */
  handler: (event: KeyboardEvent) => void;
  /** When false, the binding is inactive (useful for context-dependent
   * shortcuts). Defaults to true. */
  enabled?: boolean;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return false;
}

export function useKeyboard(bindings: Binding[]): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const inEdit = isEditable(e.target);
      for (const b of bindings) {
        if (b.enabled === false) continue;
        const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
        const want = b.key.length === 1 ? b.key.toLowerCase() : b.key;
        if (key !== want) continue;
        if (!!b.meta !== (e.metaKey || e.ctrlKey)) continue;
        if (!!b.shift !== e.shiftKey) continue;
        if (inEdit && !b.allowInInput && !b.meta) continue;
        b.handler(e);
        break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [bindings]);
}
